import { useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Bug, LogOut, MoreVertical, Search, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useActiveCallStore } from '@/stores/active-call.store';
import { authApi } from '@/features/auth/auth.api';
import { useBookings } from '@/features/bookings/useBookings';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { LangToggle } from '@/components/chassis/LangToggle';
import { NotificationsBell } from '@/components/NotificationsBell';
import { BugReportModal } from '@/components/BugReportButton';
import { useChatUi } from '@/features/chat/chatStore';
import { PatientSearchBar } from '@/components/layout/PatientSearchBar';
import { MobileNavDrawer } from '@/components/MobileNavDrawer';

/**
 * F5-R8a · «Agente v2 — Header» (dibujo del CEO, 2026-08-29): el chasis del
 * NavigatorShell aplicado al agente. Barra blanca 72px radio 14 con la MISMA
 * sombra, la marca es un botón A INICIO (aria «Ir a Inicio», tooltip) — no a
 * la cola —, pestañas de texto con subrayado teal deslizante (280ms,
 * cubic-bezier(.4,0,.2,1)) con badge de pendientes en «Inicio», la búsqueda
 * DENTRO de la barra (flex 220–320, atajo «/», placeholder que dice qué
 * acepta), el chip de llamada abierta (36px, texto truncado a 190 con el
 * nombre completo en title, punto latiendo), campana con punto GOLD + chat +
 * report con tooltip (44×44), y el toggle EN/ES del chasis.
 *
 * MUERE (contrato del dibujo): el chip de rol del medio y el nombre repetido
 * — el rol vive bajo la marca; quien está adentro sabe quién es. También el
 * gear de Settings en la barra: «Mi perfil y ajustes» vive en los accesos de
 * Inicio (la ruta /settings sigue viva).
 *
 * Adaptación del chip (mismo patrón E-16 del navigator): con llamada abierta
 * «Cerrar sesión» pasa a solo-ícono. Lo que JAMÁS cede: el toggle de idioma
 * y la búsqueda (una llamada puede empezar en español y seguir en inglés).
 *
 * El chip lee el active-call store TAL CUAL (cero lógica nueva): la
 * reconciliación R2 contra el server vive en Inicio (InicioPage) — F5-R9a
 * fundió «Mi cola» DENTRO de «Inicio»: la pestaña murió, su badge migró a
 * «Inicio» y su contenido vive completo en esa pantalla.
 */

/** Utilidad del header (dibujo: iconBtnStyle) — 44×44, radio 8, gris→navy. */
const ICON_BTN =
  'relative inline-flex items-center justify-center w-11 h-11 flex-none border-0 bg-transparent rounded-lg '
  + 'text-nav-slate cursor-pointer transition-colors duration-[140ms] hover:bg-[#f2f5f7] hover:text-nav-navy '
  + 'active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2';

export function AgentHeader(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const activeCall = useActiveCallStore((s) => s.current);
  const activeScreeningId = useActiveCallStore((s) => s.activeScreeningId);
  const { setOpen: setChatOpen } = useChatUi();
  const [reportOpen, setReportOpen] = useState(false);
  // F5-R11 (caminata CEO en 14" a media pantalla): el menú «⋮» guarda lo
  // secundario y la lupa guarda la búsqueda, así la barra entra en UNA fila
  // en laptops chicas y tablets. En xl+ nada cambia: todo desplegado.
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Badge de «Inicio» (F5-R9a: migró de la pestaña «Mi cola» muerta —
  // «no vas a eliminar nada»): llamadas (citas) pendientes de HOY — la misma
  // derivación (vivas + gracia de 5 min), sobre la MISMA queryKey
  // ['bookings.me','today'] que Inicio (cero fetch extra).
  const bookingsTodayQ = useBookings('today');
  const pendingToday = useMemo(() => {
    const rows = bookingsTodayQ.data ?? [];
    return rows.filter(
      (b) =>
        (b.status === 'scheduled' || b.status === 'rescheduled') &&
        new Date(b.scheduledAt).getTime() > Date.now() - 5 * 60_000,
    ).length;
  }, [bookingsTodayQ.data]);

  const hasCall = !!activeCall;
  const callName = activeCall
    ? `${activeCall.client.firstName} ${activeCall.client.lastName}`.trim()
    : '';

  const handleResume = (): void => {
    if (!activeCall) return;
    if (activeScreeningId) navigate(`/screening/${activeScreeningId}`);
    else navigate(`/pre-call/${activeCall.assignmentId}`);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      // ignore — still clear local
    } finally {
      clearSession();
      navigate('/login', { replace: true });
    }
  };

  return (
    <header className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-4">
      {/* barStyle del dibujo: gap 11 · min-w 0 · blanco · radio 14 · padding
          0 20 0 14 · alto 72 · la MISMA sombra del navigator · fadeUp .45s.
          GC-3 #1: sin overflow-x-auto (encerraría el dropdown de la campana).
          F5-R9m (caminata CEO, pantalla dividida): TODOS los hijos eran
          flex-none, así que el min-content de la barra (~1075px) empujaba el
          scroll horizontal de la PÁGINA y los márgenes se veían corridos. Dos
          defensas: (a) `flex-wrap` con alto MÍNIMO — si no entra, baja de
          línea y jamás hay scroll lateral; (b) degradación por breakpoints
          abajo (texto de marca, búsqueda, «Cerrar sesión», reportar). En
          pantalla ancha nada cambia: sigue una fila de 72px exactos. */}
      <div className="relative z-30 flex flex-wrap items-center gap-[11px] min-w-0 bg-white rounded-[14px] pl-3.5 pr-5 py-2 sm:py-0 min-h-[72px] shadow-[0_1px_2px_rgba(18,43,68,.06),0_8px_24px_rgba(18,43,68,.08)] animate-fade-up-slow">
        {/* La marca va a INICIO — no a una sección (contrato del dibujo). */}
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('agentF5.r8a.brandAria')}
          title={t('agentF5.r8a.brandTitle')}
          className="inline-flex items-center gap-[11px] flex-none min-h-[44px] pl-1 pr-2.5 border-0 bg-transparent rounded-lg cursor-pointer transition-colors duration-[140ms] hover:bg-[#f2f5f7] active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2"
        >
          <img src="/mark.png" alt="" className="h-[30px] w-auto flex-none" />
          {/* F5-R9m: el nombre cede primero — el logo alcanza para saber
              dónde estás y libera ~75px en pantalla angosta. */}
          <span className="hidden xl:block text-left">
            <span className="block font-head text-[13.5px] font-extrabold leading-[1.2] text-nav-navy">
              Pathway
            </span>
            <span className="block font-head text-[10px] font-bold tracking-[.16em] uppercase text-nav-slate">
              {t('header.role.agent')}
            </span>
          </span>
        </button>

        <div className="hidden sm:block w-px h-[26px] bg-nav-card flex-none" aria-hidden />

        {/* Las tres pestañas: Inicio (badge) · Mi agenda · Mis números.
            F5-R9a: «Mi cola» murió — su contenido vive DENTRO de Inicio. */}
        <nav className="flex items-center gap-[2px]">
          <AgentTab to="/" end label={t('agentF5.r8a.tabInicio')} badge={pendingToday} />
          <AgentTab to="/agenda" label={t('agentF5.r8a.tabAgenda')} />
          <AgentTab to="/numeros" label={t('agentF5.r8a.tabNumbers')} />
        </nav>

        {/* La búsqueda, EN la barra (flex 150–320, atajo «/»).
            F5-R11: bajo `md` ya no DESAPARECE — se guarda tras una lupa que
            la abre a fila completa (encargo del CEO: «también quiero que uses
            la lupita»), así el agente no pierde la función en una tablet.
            UN SOLO buscador: sólo cambia de forma. Montar dos (uno oculto por
            CSS) duplicaría el atajo «/», el debounce y el AbortController —
            medido en la caminata de verificación. El `key` lo remonta cuando
            la lupa lo abre, que es lo que le da el foco. */}
        <PatientSearchBar
          key={searchOpen ? 'search-open' : 'search-bar'}
          variant="bar"
          autoFocus={searchOpen}
          className={searchOpen ? 'basis-full min-w-0 !max-w-none order-last' : 'hidden md:block'}
          placeholder={t('agentF5.r8a.searchPlaceholder')}
          ariaLabel={t('agentF5.r8a.searchAria')}
        />
        <button
          type="button"
          onClick={() => setSearchOpen((v) => !v)}
          aria-label={t('agentF5.r11.searchToggle')}
          title={t('agentF5.r11.searchToggle')}
          aria-expanded={searchOpen}
          className={cn(ICON_BTN, 'md:hidden')}
        >
          {searchOpen
            ? <X className="h-[18px] w-[18px] flex-none" aria-hidden />
            : <Search className="h-[18px] w-[18px] flex-none" aria-hidden />}
        </button>

        {/* Chip de llamada abierta — 36px, texto truncado a 190, nombre
            completo en title, punto latiendo (pulseDot 2s). Es el ÚNICO hijo
            que encoge (flex 0 1 auto + min-w-0). */}
        {hasCall && (
          <button
            type="button"
            onClick={handleResume}
            title={t('agentF5.r8a.callChipTitle', { name: callName })}
            className="inline-flex items-center gap-2 flex-[0_1_auto] min-w-0 min-h-[36px] px-[13px] rounded-full bg-nav-tealTint border-[1.5px] border-nav-teal font-head text-[12.5px] font-bold text-nav-tealDark cursor-pointer transition-colors duration-[140ms] hover:bg-[#d3e8e9] hover:border-nav-tealDark active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2"
          >
            <span aria-hidden className="flex-none w-2 h-2 rounded-full bg-nav-teal animate-pulse-dot" />
            <span className="max-w-[190px] truncate">
              {t('agentF5.r8a.callChip', { name: callName })}
            </span>
          </button>
        )}

        <div className="ml-auto flex items-center gap-[2px] flex-none">
          {/* Campana compartida (poll + toasts + panel), vestida al dibujo
              desde el contenedor (patrón NavigatorShell): 44×44 radio 8,
              ícono 18, punto GOLD 7px con borde blanco en 10/10. */}
          {user && (
            <span className="flex-none [&>div>button]:w-11 [&>div>button]:h-11 [&>div>button]:rounded-lg [&>div>button]:text-nav-slate [&>div>button:hover]:bg-[#f2f5f7] [&>div>button:hover]:text-nav-navy [&>div>button>svg]:h-[18px] [&>div>button>svg]:w-[18px] [&>div>button>span]:bg-nav-gold [&>div>button>span]:h-[7px] [&>div>button>span]:w-[7px] [&>div>button>span]:top-[10px] [&>div>button>span]:right-[10px] [&>div>button>span]:border-[1.5px] [&>div>button>span]:border-white">
              <NotificationsBell />
            </span>
          )}
          {/* F5-R11: chat y reportar viven sueltos sólo en xl+; abajo se
              guardan en el menú «⋮» (la campana JAMÁS se guarda: es el aviso). */}
          <button
            type="button"
            aria-label={t('agentF5.r8a.chat')}
            title={t('agentF5.r8a.chat')}
            onClick={() => setChatOpen(true)}
            className={cn(ICON_BTN, 'hidden xl:inline-flex')}
          >
            <ChatIcon />
          </button>
          <button
            type="button"
            aria-label={t('agentF5.r8a.report')}
            title={t('agentF5.r8a.report')}
            onClick={() => setReportOpen(true)}
            className={cn(ICON_BTN, 'hidden xl:inline-flex')}
          >
            {/* El escarabajo (encargo del CEO): es el ícono que el sistema ya
                usa para reportar en BugReportButton — deja de ser el «i». */}
            <Bug className="h-[18px] w-[18px] flex-none" aria-hidden />
          </button>
          {/* El menú «⋮»: sólo cuando NO hay sitio para los sueltos. */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t('agentF5.r11.menu')}
            title={t('agentF5.r11.menu')}
            className={cn(ICON_BTN, 'xl:hidden')}
          >
            <MoreVertical className="h-[18px] w-[18px] flex-none" aria-hidden />
          </button>
        </div>

        <div className="hidden xl:block w-px h-[26px] bg-nav-card flex-none" aria-hidden />

        {/* «Cerrar sesión» y el idioma: sueltos sólo en xl+; abajo viven en el
            menú «⋮». Con el chip de llamada el botón pasa a solo-ícono (E-16). */}
        {user && (
          <button
            type="button"
            onClick={() => { void handleLogout(); }}
            title={t('header.signOut')}
            aria-label={t('header.signOut')}
            className={
              hasCall
                ? cn(ICON_BTN, 'hidden xl:inline-flex text-nav-teal hover:bg-nav-tealTint hover:text-nav-tealDark')
                : 'hidden xl:inline-flex items-center justify-center gap-[7px] flex-none whitespace-nowrap min-h-[44px] px-[11px] border-0 bg-transparent rounded-lg font-head font-semibold text-[13.5px] text-nav-teal cursor-pointer transition-colors duration-[140ms] hover:bg-nav-tealTint hover:text-nav-tealDark active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2'
            }
          >
            <LogOut className={hasCall ? 'h-[18px] w-[18px] flex-none' : 'h-[17px] w-[17px] flex-none'} aria-hidden />
            {!hasCall && <span>{t('header.signOut')}</span>}
          </button>
        )}

        <div className="hidden xl:block flex-none">
          <LangToggle />
        </div>

      </div>

      {/* El cajón del «⋮» — la MISMA pieza que ya usan admin y uploader. */}
      <MobileNavDrawer
        upTo="xl"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        ariaLabel={t('agentF5.r11.menu')}
        title={user?.fullName ?? t('header.role.agent')}
      >
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setChatOpen(true); }}
            className={DRAWER_ITEM}
          >
            <ChatIcon />
            <span>{t('agentF5.r8a.chat')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setReportOpen(true); }}
            className={DRAWER_ITEM}
          >
            <Bug className="h-[18px] w-[18px] flex-none" aria-hidden />
            <span>{t('agentF5.r8a.report')}</span>
          </button>
          <div className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="font-head text-[13.5px] font-semibold text-nav-slate">
              {t('agentF5.r11.language')}
            </span>
            <LangToggle />
          </div>
          {user && (
            <button
              type="button"
              onClick={() => { setMenuOpen(false); void handleLogout(); }}
              className={cn(DRAWER_ITEM, 'text-nav-teal')}
            >
              <LogOut className="h-[18px] w-[18px] flex-none" aria-hidden />
              <span>{t('header.signOut')}</span>
            </button>
          )}
        </div>
      </MobileNavDrawer>

      {reportOpen && <BugReportModal onClose={() => setReportOpen(false)} />}
    </header>
  );
}

/** Fila del cajón: 44px táctil, mismo lenguaje que los ítems del uploader. */
const DRAWER_ITEM =
  'inline-flex items-center gap-3 w-full min-h-[44px] px-3 rounded-lg border-0 bg-transparent '
  + 'font-head text-[13.5px] font-semibold text-nav-navy text-left cursor-pointer '
  + 'transition-colors duration-[140ms] hover:bg-nav-hair active:scale-[0.98] '
  + 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2';

/**
 * La pestaña del dibujo: texto Montserrat 14.5 (600 inactiva / 700 activa),
 * badge de pendientes (navy/blanco activa · hair/slate inactiva, tabular),
 * y el subrayado teal que se desliza desde la izquierda (scaleX 0→1, 280ms,
 * cubic-bezier(.4,0,.2,1)). `aria-current` lo pone NavLink; title = label
 * (dibujo). Press .97 como el chasis del navigator.
 */
function AgentTab({
  to,
  label,
  end,
  badge,
}: {
  to: string;
  label: string;
  end?: boolean;
  badge?: number;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      title={label}
      className="group relative inline-flex items-center gap-2 flex-none whitespace-nowrap bg-transparent border-0 px-2 sm:px-3 pt-[10px] pb-[13px] no-underline font-head text-[14.5px] tracking-[-0.005em] cursor-pointer transition-colors active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-nav-teal focus-visible:outline-offset-2"
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'transition-colors duration-[160ms]',
              isActive ? 'font-bold text-nav-navy' : 'font-semibold text-nav-slate group-hover:text-nav-navy',
            )}
          >
            {label}
          </span>
          {badge != null && badge > 0 && (
            <span
              className={cn(
                'rounded-full px-2 py-[2px] font-head text-[10.5px] font-extrabold tabular-nums',
                isActive ? 'bg-nav-navy text-white' : 'bg-nav-hair text-nav-slate',
              )}
            >
              {badge}
            </span>
          )}
          <span
            aria-hidden
            className={cn(
              'absolute left-3 right-3 bottom-1 h-[2px] rounded-[2px] bg-nav-teal origin-left',
              'transition-transform duration-[280ms] ease-[cubic-bezier(.4,0,.2,1)]',
              isActive ? 'scale-x-100' : 'scale-x-0',
            )}
          />
        </>
      )}
    </NavLink>
  );
}

/** El ícono de chat del dibujo (mismo path que el chasis del navigator). */
function ChatIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-none">
      <path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H10l-4.5 4v-4H4A1.5 1.5 0 0 1 2.5 16V7A1.5 1.5 0 0 1 4 5.5Z" />
    </svg>
  );
}
